import { zodToJsonSchema } from "zod-to-json-schema";
import { WIDGET_KINDS } from "@playbookai/types";
import { ProblemStatementParamsSchema, defaultProblemStatementParams, } from "./problemStatement.js";
import { RetiredTagsParamsV2Schema, TagsParamsSchema, defaultTagsParams, } from "./tags.js";
import { RunChartParamsSchema, RetiredRunChartParamsV1Schema, defaultRunChartParams, } from "./runChart.js";
import { KpiTileParamsSchema, defaultKpiTileParams, RetiredKpiTileParamsV1Schema, } from "./kpiTile.js";
import { KpiCardParamsSchema, defaultKpiCardParams } from "./kpiCard.js";
import { ProblemTreeParamsSchema, defaultProblemTreeParams, } from "./problemTree.js";
import { CurrentStateStatsParamsSchema, defaultCurrentStateStatsParams, } from "./currentStateStats.js";
import { ContainmentCardsParamsSchema, defaultContainmentCardsParams, } from "./containmentCards.js";
import { ProblemTimelineParamsSchema, defaultProblemTimelineParams, } from "./problemTimeline.js";
import { ContainmentPlanParamsSchema, defaultContainmentPlanParams, } from "./containmentPlan.js";
import { ExposureRiskReviewParamsSchema, defaultExposureRiskReviewParams, } from "./exposureRiskReview.js";
import { ParetoL1ParamsSchema, ParetoL2ParamsSchema, defaultParetoL1Params, defaultParetoL2Params, } from "./pareto.js";
import { FunnelParamsSchema, defaultFunnelParams } from "./funnel.js";
import { CausalChainParamsSchema, RetiredCausalChainParamsV1Schema, defaultCausalChainParams, } from "./causalChain.js";
import { FishboneParamsSchema, defaultFishboneParams } from "./fishbone.js";
import { ProcessTraceParamsSchema, defaultProcessTraceParams, } from "./processTrace.js";
import { ImpactEffortParamsSchema, defaultImpactEffortParams, } from "./impactEffort.js";
import { CountermeasureTableParamsSchema, defaultCountermeasureTableParams, } from "./countermeasureTable.js";
import { StakeholderMapParamsSchema, defaultStakeholderMapParams, } from "./stakeholderMap.js";
import { ControlPlanParamsSchema, defaultControlPlanParams, } from "./controlPlan.js";
import { EscalationMatrixParamsSchema, RetiredEscalationMatrixParamsV1Schema, defaultEscalationMatrixParams, } from "./escalationMatrix.js";
import { GenericTableParamsSchema, defaultGenericTableParams, } from "./genericTable.js";
import { MarkdownTextParamsSchema, defaultMarkdownTextParams, } from "./markdownText.js";
import { SipocParamsSchema, defaultSipocParams } from "./sipoc.js";
import { GoalsParamsSchema, defaultGoalsParams } from "./goals.js";
import { CharterParamsSchema, defaultCharterParams } from "./charter.js";
import { ProjectContextParamsSchema, defaultProjectContextParams, } from "./projectContext.js";
import { KpiChartParamsSchema, defaultSingleKpiParams, defaultChartParams, } from "./kpiChart.js";
import { KpiWatchlistParamsSchema, defaultKpiWatchlistParams, } from "./kpiWatchlist.js";
import { KpiTableParamsSchema, defaultKpiTableParams } from "./kpiTable.js";
import { MyTasksParamsSchema, defaultMyTasksParams } from "./myTasks.js";
import { ProjectPulseParamsSchema, defaultProjectPulseParams, } from "./projectPulse.js";
import { StrategicHealthParamsSchema, defaultStrategicHealthParams, } from "./strategicHealth.js";
import { DailyBriefParamsSchema, defaultDailyBriefParams, } from "./dailyBrief.js";
import { CoachingNudgeParamsSchema, defaultCoachingNudgeParams, } from "./coachingNudge.js";
import { MoodCheckParamsSchema, defaultMoodCheckParams } from "./moodCheck.js";
import { SolveActionsParamsSchema, defaultSolveActionsParams, } from "./solveActions.js";
import { MySolvesParamsSchema, MyVsmsParamsSchema, QuickAccessParamsSchema, defaultMySolvesParams, defaultMyVsmsParams, defaultQuickAccessParams, } from "./homeNavigation.js";
import { KaizenHealthParamsSchema, KaizenTableParamsSchema, defaultKaizenHealthParams, defaultKaizenTableParams, } from "./dashboardKaizen.js";
import { TextCardParamsSchema, defaultTextCardParams } from "./textCard.js";
import { ImageParamsSchema, ImageTileParamsSchema, defaultImageParams, defaultImageTileParams, } from "./image.js";
import { LamaMaturityProfileParamsSchema, LamaMaturityTrendParamsSchema, LamaParticipationTrendParamsSchema, LamaPillarResultsParamsSchema, LamaPriorityRoadmapParamsSchema, LamaReportOverviewParamsSchema, LamaTeamHeatmapParamsSchema, LamaTopMoversParamsSchema, defaultLamaMaturityProfileParams, defaultLamaMaturityTrendParams, defaultLamaParticipationTrendParams, defaultLamaPillarResultsParams, defaultLamaPriorityRoadmapParams, defaultLamaReportOverviewParams, defaultLamaTeamHeatmapParams, defaultLamaTopMoversParams, LamaMaturityHierarchyParamsSchema, defaultLamaMaturityHierarchyParams, LamaHierarchyResultsParamsSchema, defaultLamaHierarchyResultsParams, } from "./lamaReport.js";
import { LinkCardParamsSchema, defaultLinkCardParams } from "./linkCard.js";
/** Per-kind Zod schemas — single source of truth for widget params. */
export const WIDGET_PARAMS_SCHEMAS = {
    problemStatement: ProblemStatementParamsSchema,
    tags: TagsParamsSchema,
    runChart: RunChartParamsSchema,
    kpiTile: KpiTileParamsSchema,
    problemTree: ProblemTreeParamsSchema,
    currentStateStats: CurrentStateStatsParamsSchema,
    containmentCards: ContainmentCardsParamsSchema,
    problemTimeline: ProblemTimelineParamsSchema,
    containmentPlan: ContainmentPlanParamsSchema,
    exposureRiskReview: ExposureRiskReviewParamsSchema,
    paretoL1: ParetoL1ParamsSchema,
    paretoL2: ParetoL2ParamsSchema,
    funnel: FunnelParamsSchema,
    causalChain: CausalChainParamsSchema,
    fishbone: FishboneParamsSchema,
    processTrace: ProcessTraceParamsSchema,
    impactEffort: ImpactEffortParamsSchema,
    countermeasureTable: CountermeasureTableParamsSchema,
    stakeholderMap: StakeholderMapParamsSchema,
    controlPlan: ControlPlanParamsSchema,
    escalationMatrix: EscalationMatrixParamsSchema,
    genericTable: GenericTableParamsSchema,
    markdownText: MarkdownTextParamsSchema,
    imageTile: ImageTileParamsSchema,
    linkTile: LinkCardParamsSchema,
    // VSM kinds.
    sipoc: SipocParamsSchema,
    goals: GoalsParamsSchema,
    charter: CharterParamsSchema,
    projectContext: ProjectContextParamsSchema,
    // Dashboard kinds (ADR-0015). singleKpi + chart share KpiChartParamsSchema.
    singleKpi: KpiChartParamsSchema,
    chart: KpiChartParamsSchema,
    kpiWatchlist: KpiWatchlistParamsSchema,
    kpiTable: KpiTableParamsSchema,
    myTasks: MyTasksParamsSchema,
    projectPulse: ProjectPulseParamsSchema,
    strategicHealth: StrategicHealthParamsSchema,
    dailyBrief: DailyBriefParamsSchema,
    coachingNudge: CoachingNudgeParamsSchema,
    moodCheck: MoodCheckParamsSchema,
    mySolves: MySolvesParamsSchema,
    myVsms: MyVsmsParamsSchema,
    quickAccess: QuickAccessParamsSchema,
    kaizenTable: KaizenTableParamsSchema,
    kaizenHealth: KaizenHealthParamsSchema,
    solveActions: SolveActionsParamsSchema,
    // Hoshin canvas Yjs kinds (PB-234). kpiCard keeps the pre-binding tile shape
    // (`KpiCardParamsSchema`) now that `kpiTile` binds to KPI Manager.
    textCard: TextCardParamsSchema,
    image: ImageParamsSchema,
    linkCard: LinkCardParamsSchema,
    kpiCard: KpiCardParamsSchema,
    // LAMA report dashboard kinds (ADR-0054): presentation-only ({} strict) —
    // report content rides the widget data connection, never the params.
    lamaReportOverview: LamaReportOverviewParamsSchema,
    lamaMaturityProfile: LamaMaturityProfileParamsSchema,
    lamaMaturityTrend: LamaMaturityTrendParamsSchema,
    lamaParticipationTrend: LamaParticipationTrendParamsSchema,
    lamaCategoryResults: LamaPillarResultsParamsSchema,
    lamaPillarResults: LamaPillarResultsParamsSchema,
    lamaMaturityHierarchy: LamaMaturityHierarchyParamsSchema,
    lamaHierarchyResults: LamaHierarchyResultsParamsSchema,
    lamaTeamHeatmap: LamaTeamHeatmapParamsSchema,
    lamaTopMovers: LamaTopMoversParamsSchema,
    lamaPriorityRoadmap: LamaPriorityRoadmapParamsSchema,
};
/** Public persisted-schema version for newly-created rows. */
export const WIDGET_CURRENT_SCHEMA_VERSIONS = {
    ...Object.fromEntries(WIDGET_KINDS.map((kind) => [kind, 1])),
};
/** Resolve the validator pinned to a persisted widget row. */
export function getWidgetParamsSchema(kind, schemaVersion = WIDGET_CURRENT_SCHEMA_VERSIONS[kind]) {
    const current = WIDGET_PARAMS_SCHEMAS[kind];
    if (!current) {
        throw new Error(`No schema registered for widget kind '${kind}'.`);
    }
    if (schemaVersion === WIDGET_CURRENT_SCHEMA_VERSIONS[kind]) {
        return current;
    }
    throw new Error(`No params schema registered for widget kind '${kind}' version ${schemaVersion}.`);
}
/** Per-kind seed defaults — used by `defaultParams` on each WidgetDef. */
export const WIDGET_DEFAULT_PARAMS = {
    problemStatement: defaultProblemStatementParams,
    tags: defaultTagsParams,
    runChart: defaultRunChartParams,
    kpiTile: defaultKpiTileParams,
    problemTree: defaultProblemTreeParams,
    currentStateStats: defaultCurrentStateStatsParams,
    containmentCards: defaultContainmentCardsParams,
    problemTimeline: defaultProblemTimelineParams,
    containmentPlan: defaultContainmentPlanParams,
    exposureRiskReview: defaultExposureRiskReviewParams,
    paretoL1: defaultParetoL1Params,
    paretoL2: defaultParetoL2Params,
    funnel: defaultFunnelParams,
    causalChain: defaultCausalChainParams,
    fishbone: defaultFishboneParams,
    processTrace: defaultProcessTraceParams,
    impactEffort: defaultImpactEffortParams,
    countermeasureTable: defaultCountermeasureTableParams,
    stakeholderMap: defaultStakeholderMapParams,
    controlPlan: defaultControlPlanParams,
    escalationMatrix: defaultEscalationMatrixParams,
    genericTable: defaultGenericTableParams,
    markdownText: defaultMarkdownTextParams,
    imageTile: defaultImageTileParams,
    linkTile: defaultLinkCardParams,
    sipoc: defaultSipocParams,
    goals: defaultGoalsParams,
    charter: defaultCharterParams,
    projectContext: defaultProjectContextParams,
    singleKpi: defaultSingleKpiParams,
    chart: defaultChartParams,
    kpiWatchlist: defaultKpiWatchlistParams,
    kpiTable: defaultKpiTableParams,
    myTasks: defaultMyTasksParams,
    projectPulse: defaultProjectPulseParams,
    strategicHealth: defaultStrategicHealthParams,
    dailyBrief: defaultDailyBriefParams,
    coachingNudge: defaultCoachingNudgeParams,
    moodCheck: defaultMoodCheckParams,
    mySolves: defaultMySolvesParams,
    myVsms: defaultMyVsmsParams,
    quickAccess: defaultQuickAccessParams,
    kaizenTable: defaultKaizenTableParams,
    kaizenHealth: defaultKaizenHealthParams,
    solveActions: defaultSolveActionsParams,
    textCard: defaultTextCardParams,
    image: defaultImageParams,
    linkCard: defaultLinkCardParams,
    kpiCard: defaultKpiCardParams,
    lamaReportOverview: defaultLamaReportOverviewParams,
    lamaMaturityProfile: defaultLamaMaturityProfileParams,
    lamaMaturityTrend: defaultLamaMaturityTrendParams,
    lamaParticipationTrend: defaultLamaParticipationTrendParams,
    lamaCategoryResults: defaultLamaPillarResultsParams,
    lamaPillarResults: defaultLamaPillarResultsParams,
    lamaMaturityHierarchy: defaultLamaMaturityHierarchyParams,
    lamaHierarchyResults: defaultLamaHierarchyResultsParams,
    lamaTeamHeatmap: defaultLamaTeamHeatmapParams,
    lamaTopMovers: defaultLamaTopMoversParams,
    lamaPriorityRoadmap: defaultLamaPriorityRoadmapParams,
};
/**
 * Storage discriminant per kind — the single source of truth for the
 * Yjs/SQL split (PB-234). `"sql"` kinds are entity-backed (SOLVE cards +
 * dashboard widgets) and entity-creatable; `"yjs"` kinds live inline in the
 * canvas Y.Doc and are NOT entity-creatable. `ENTITY_WIDGET_KINDS`
 * (playbook-be) derives from this, and a `packages/ui` test asserts each
 * registered `WidgetDef.storage` matches it —
 * so a future Yjs kind can never silently become entity-creatable.
 */
export const WIDGET_STORAGE = {
    problemStatement: "sql",
    tags: "sql",
    runChart: "sql",
    kpiTile: "sql",
    problemTree: "sql",
    currentStateStats: "sql",
    containmentCards: "sql",
    problemTimeline: "sql",
    containmentPlan: "sql",
    exposureRiskReview: "sql",
    paretoL1: "sql",
    paretoL2: "sql",
    funnel: "sql",
    causalChain: "sql",
    fishbone: "sql",
    processTrace: "sql",
    impactEffort: "sql",
    countermeasureTable: "sql",
    stakeholderMap: "sql",
    controlPlan: "sql",
    escalationMatrix: "sql",
    genericTable: "sql",
    markdownText: "sql",
    imageTile: "sql",
    linkTile: "sql",
    sipoc: "sql",
    goals: "sql",
    charter: "sql",
    projectContext: "sql",
    singleKpi: "sql",
    chart: "sql",
    kpiWatchlist: "sql",
    kpiTable: "sql",
    myTasks: "sql",
    projectPulse: "sql",
    strategicHealth: "sql",
    dailyBrief: "sql",
    coachingNudge: "sql",
    moodCheck: "sql",
    mySolves: "sql",
    myVsms: "sql",
    quickAccess: "sql",
    kaizenTable: "sql",
    kaizenHealth: "sql",
    solveActions: "sql",
    textCard: "yjs",
    image: "yjs",
    linkCard: "yjs",
    kpiCard: "yjs",
    // LAMA report dashboard kinds are relational ("sql" rendering/storage mode)
    // but dashboard-instance-only — see WIDGET_INSTANCE_STORES below.
    lamaReportOverview: "sql",
    lamaMaturityProfile: "sql",
    lamaMaturityTrend: "sql",
    lamaParticipationTrend: "sql",
    lamaCategoryResults: "sql",
    lamaPillarResults: "sql",
    lamaMaturityHierarchy: "sql",
    lamaHierarchyResults: "sql",
    lamaTeamHeatmap: "sql",
    lamaTopMovers: "sql",
    lamaPriorityRoadmap: "sql",
};
const DASHBOARD_ONLY = ["dashboard_instance"];
const ENTITY_AND_DASHBOARD = [
    "widget_entity",
    "dashboard_instance",
];
const ENTITY_ONLY = ["widget_entity"];
const YJS_ONLY = ["canvas_yjs"];
export const WIDGET_INSTANCE_STORES = {
    problemStatement: ENTITY_ONLY,
    tags: ENTITY_ONLY,
    runChart: ENTITY_ONLY,
    kpiTile: ENTITY_ONLY,
    problemTree: ENTITY_ONLY,
    currentStateStats: ENTITY_ONLY,
    containmentCards: ENTITY_ONLY,
    problemTimeline: ENTITY_ONLY,
    containmentPlan: ENTITY_ONLY,
    exposureRiskReview: ENTITY_ONLY,
    paretoL1: ENTITY_ONLY,
    paretoL2: ENTITY_ONLY,
    funnel: ENTITY_ONLY,
    causalChain: ENTITY_ONLY,
    fishbone: ENTITY_ONLY,
    processTrace: ENTITY_ONLY,
    impactEffort: ENTITY_ONLY,
    countermeasureTable: ENTITY_ONLY,
    stakeholderMap: ENTITY_ONLY,
    controlPlan: ENTITY_ONLY,
    escalationMatrix: ENTITY_ONLY,
    genericTable: ENTITY_ONLY,
    markdownText: ENTITY_ONLY,
    imageTile: ENTITY_ONLY,
    linkTile: ENTITY_ONLY,
    sipoc: ENTITY_ONLY,
    goals: ENTITY_ONLY,
    charter: ENTITY_ONLY,
    projectContext: ENTITY_ONLY,
    singleKpi: ENTITY_AND_DASHBOARD,
    chart: ENTITY_AND_DASHBOARD,
    kpiWatchlist: ENTITY_AND_DASHBOARD,
    kpiTable: ENTITY_AND_DASHBOARD,
    myTasks: ENTITY_AND_DASHBOARD,
    projectPulse: ENTITY_AND_DASHBOARD,
    strategicHealth: ENTITY_AND_DASHBOARD,
    dailyBrief: ENTITY_AND_DASHBOARD,
    coachingNudge: ENTITY_AND_DASHBOARD,
    moodCheck: ENTITY_AND_DASHBOARD,
    mySolves: DASHBOARD_ONLY,
    myVsms: DASHBOARD_ONLY,
    quickAccess: DASHBOARD_ONLY,
    kaizenTable: DASHBOARD_ONLY,
    kaizenHealth: DASHBOARD_ONLY,
    solveActions: DASHBOARD_ONLY,
    textCard: YJS_ONLY,
    image: YJS_ONLY,
    linkCard: YJS_ONLY,
    kpiCard: YJS_ONLY,
    lamaReportOverview: DASHBOARD_ONLY,
    lamaMaturityProfile: DASHBOARD_ONLY,
    lamaMaturityTrend: DASHBOARD_ONLY,
    lamaParticipationTrend: DASHBOARD_ONLY,
    lamaCategoryResults: DASHBOARD_ONLY,
    lamaPillarResults: DASHBOARD_ONLY,
    lamaMaturityHierarchy: DASHBOARD_ONLY,
    lamaHierarchyResults: DASHBOARD_ONLY,
    lamaTeamHeatmap: DASHBOARD_ONLY,
    lamaTopMovers: DASHBOARD_ONLY,
    lamaPriorityRoadmap: DASHBOARD_ONLY,
};
/**
 * Kinds that are entity-creatable (`POST /v1/widgets`) + AI-advertised.
 * Derives from the instance-store contract, NOT from `WIDGET_STORAGE`: the
 * LAMA report kinds are relational ("sql") but dashboard-instance-only, so a
 * storage-based filter would silently make them entity-creatable (ADR-0054).
 * Membership for every pre-existing kind is unchanged.
 */
export const ENTITY_WIDGET_KINDS = WIDGET_KINDS.filter((kind) => WIDGET_INSTANCE_STORES[kind].includes("widget_entity"));
/**
 * Entity kinds intentionally exposed to AI/tool discovery. Hoshin's shared
 * image/link widgets are user-authored only; adding them for Hoshin must not
 * silently broaden the Player AI tool surface.
 */
export const AI_DISCOVERABLE_WIDGET_KINDS = ENTITY_WIDGET_KINDS.filter((kind) => kind !== "imageTile" && kind !== "linkTile");
/** Kinds persistable as dashboard.widget_instances rows (ADR-0054). */
export const DASHBOARD_INSTANCE_WIDGET_KINDS = WIDGET_KINDS.filter((kind) => WIDGET_INSTANCE_STORES[kind].includes("dashboard_instance"));
/** Human-readable label per kind. */
export const WIDGET_LABELS = {
    problemStatement: "Problem Statement",
    tags: "Tags",
    runChart: "Run Chart",
    kpiTile: "KPI Tile",
    problemTree: "Problem Tree",
    currentStateStats: "Current-State Snapshot",
    containmentCards: "Containment Cards",
    problemTimeline: "Problem Timeline",
    containmentPlan: "Containment Action Plan",
    exposureRiskReview: "Exposure & Risk Review",
    paretoL1: "Level 1 Pareto",
    paretoL2: "Level 2 Pareto",
    funnel: "Funnel Analysis",
    causalChain: "5 Whys Analysis",
    fishbone: "Fishbone (Ishikawa)",
    processTrace: "Causal Chain Analysis",
    impactEffort: "Impact / Effort Matrix",
    countermeasureTable: "Countermeasure Plan",
    stakeholderMap: "Stakeholder Map",
    controlPlan: "Control Plan",
    escalationMatrix: "Escalation Matrix",
    genericTable: "Structured Table",
    markdownText: "Text card",
    imageTile: "Image",
    linkTile: "Link card",
    sipoc: "SIPOC",
    goals: "Goals",
    charter: "VSM Charter",
    projectContext: "VSM Project Context",
    singleKpi: "Single KPI",
    chart: "Chart",
    kpiWatchlist: "KPI Watchlist",
    kpiTable: "KPI Table",
    myTasks: "My Tasks",
    projectPulse: "Project Pulse",
    strategicHealth: "Strategic Health",
    dailyBrief: "Daily Brief",
    coachingNudge: "Coaching Nudge",
    moodCheck: "Mood Check",
    mySolves: "My SOLVEs",
    myVsms: "My VSMs",
    quickAccess: "Quick Access",
    kaizenTable: "Kaizen Table",
    kaizenHealth: "Kaizen Health",
    solveActions: "SOLVE Actions",
    textCard: "Text card",
    image: "Image",
    linkCard: "Link card",
    kpiCard: "KPI tile",
    lamaReportOverview: "Report Overview",
    lamaMaturityProfile: "Maturity Profile",
    lamaMaturityTrend: "Maturity Trend",
    lamaParticipationTrend: "Participation by Wave",
    lamaCategoryResults: "Results by Category",
    lamaPillarResults: "Results by Pillar",
    lamaMaturityHierarchy: "Maturity Overview",
    lamaHierarchyResults: "Results by Pillar and Category",
    lamaTeamHeatmap: "Teams Over Time",
    lamaTopMovers: "Top Movers",
    lamaPriorityRoadmap: "Priority Roadmap",
};
/** AI-prompt-friendly description per kind. */
export const WIDGET_DESCRIPTIONS = {
    problemStatement: "Frames the headline problem with a single 5W2H-style paragraph. One per Solve.",
    tags: "A concise text-based Trend × Actual × Gap × Standard snapshot of the problem state.",
    runChart: "Time-series line chart of one metric over time, with optional target reference.",
    kpiTile: "A read-only view of one KPI Manager KPI, pinned to this canvas. The tile stores only which KPI it shows and how to present it; the name, unit, current value, applicable target and history are resolved from KPI Manager and cannot be changed from here. Propose a tile by naming a KPI the requester can already read; to change a measurement or a target, use KPI Manager's own audited workflow.",
    problemTree: "The Solve's single problem tree, authored in Specify and kept current through every later phase: concise symptoms, one central problem, and potential causes, each a separate short sticky-note label. One tree per Solve — update the existing one rather than creating another. Use a faithful, concise summary in the Problem Tree centre, within 255 characters. Keep the full problem statement in the Problem Statement widget. Each cause carries an evidence status of suspected, confirmed or ruled out; move a cause only when the conversation produced evidence that supports or contradicts it, keep ruled-out causes on the tree, and repeat the stored status of every cause you are not deliberately changing.",
    currentStateStats: "Baseline snapshot: 3-column tile grid of current-state measures with deltas and tone.",
    containmentCards: "Stop-the-bleed action list with owner, due date, status pill, and progress bar.",
    problemTimeline: "Timeline of occurrence, recognition, solving, and countermeasure milestones that sizes the suspect window and exposure.",
    containmentPlan: "Containment action plan with owner, ETA, priority, status, effectiveness, notes, and review cadence.",
    exposureRiskReview: "Candidate review of immediate exposure, containment failure, and containment side-effect risks.",
    paretoL1: "First-cut Pareto bar and cumulative-percentage chart showing category contributions to a total. Use only additive values, such as claim counts or costs, with consistent units and scope and no double-counting. Do not use rates, averages, or normalized scores to calculate contribution shares. If only rates are available, compare them in a table and ask for matching counts before creating a Pareto.",
    paretoL2: "Drill-down Pareto bar and cumulative-percentage chart within the leading Level 1 category. Use only additive subcategory values, such as claim counts or costs, with consistent units and scope and no double-counting. Do not use rates, averages, or normalized scores to calculate contribution shares. If only rates are available, compare them in a table and ask for matching counts before creating a Pareto.",
    funnel: "Three-level Why funnel: broad symptoms → why questions → actionable root causes.",
    causalChain: "5 Whys Analysis: explore why a problem happened through successive why questions, with each answer explaining the preceding step. Keep unsupported causes as hypotheses; do not force exactly five steps or treat the last answer as a verified root cause.",
    fishbone: "Ishikawa diagram grouping causes by 6M categories (Man / Method / Machine / Material / Measurement / Environment).",
    processTrace: "Causal Chain Analysis: trace the problem from where it was noticed (Point of Recognition, POR), to where expected and actual behavior first diverged (Point of Occurrence, POO), to where the causal condition can be observed (Point of Cause, POC). Record evidence and uncertainty at each point; identifying a Point of Cause does not establish validation. Use this fixed evidence trace rather than successive why questions.",
    impactEffort: "Kaizen quadrant plot — items positioned on Effort × Impact, draggable to reposition.",
    countermeasureTable: "Proposed countermeasures with a short action-only title, impact/effort scoring, owner, window, and status.",
    stakeholderMap: "Power × Interest 2×2 grid plotting stakeholders into Manage / Satisfy / Inform / Monitor quadrants.",
    controlPlan: "Sustained-monitoring table: item, cadence, responsible person, trigger condition.",
    escalationMatrix: "Sparse 3×3 grid (severity × response level) defining who to escalate to when things go wrong.",
    genericTable: "Phase-agnostic structured table with typed columns and bounded rows for method outputs that do not fit a specialized widget.",
    markdownText: "Phase-agnostic Markdown document for narratives, notes, and formatted analysis.",
    imageTile: "Reference image whose binary is stored externally and whose widget params contain a hosted URL.",
    linkTile: "Quick link to a doc, dashboard, or runbook.",
    sipoc: "Suppliers · Inputs · Process · Outputs · Customers scoping grid — multi-entry rows framing the value stream's scope.",
    goals: "Baseline → target → % change table of the improvement goals a VSM charter commits to.",
    charter: "VSM workshop charter covering problem, scope, goals, takt inputs, shifts per day, and working days per week.",
    projectContext: "VSM value stream roles and the four fixed preparation outcomes used to establish project readiness.",
    singleKpi: "One pinned KPI with sparkline + AI commentary; pins a KPI by id (resolved from the KPI library).",
    chart: "Generic KPI chart card — pick a KPI and a chart variant (line / area / bar / big-number / mini-stat / trend).",
    kpiWatchlist: "Cross-plant KPI signal feed — up to six KPIs with status dot and delta.",
    kpiTable: "Multi-KPI table with sortable columns and an AI summary line.",
    myTasks: "AI-ranked personal action inbox, grouped into Do-today / Can-wait buckets.",
    projectPulse: "Active improvement projects worth glancing at right now — one per health.",
    strategicHealth: "Top-line transformation KPIs as a row of stat cards (objectives on track, red KPIs, etc.).",
    dailyBrief: "PlayerAI's morning narrative for the day, with a headline and tone-tagged highlights.",
    coachingNudge: "One short, personal PlayerAI coaching nudge with a follow-up prompt.",
    moodCheck: "Quick personal daily mood check-in (private to the user).",
    mySolves: "The five most recently active open SOLVEs the user can access, with a link to the SOLVE home.",
    myVsms: "The five most recently updated active VSMs the user can access, with a link to the VSM library.",
    quickAccess: "Shortcuts to the product modules currently enabled for the user and workspace.",
    kaizenTable: "A bounded, filterable table of Kaizens from accessible value streams.",
    kaizenHealth: "Deterministic health summary of all Kaizens in the current value stream.",
    solveActions: "Read-only view of Containment Plan actions and Countermeasure Table rows across the SOLVEs you can access.",
    textCard: "Free-form note with a title and body.",
    image: "Embed a reference image from a URL.",
    linkCard: "Quick link to a doc, dashboard, or runbook.",
    kpiCard: "Headline metric the workshop can react to.",
    lamaReportOverview: "Campaign report header: overall maturity, deltas, participation, and confidence.",
    lamaMaturityProfile: "Radar of maturity by pillar with target and previous-wave overlays.",
    lamaMaturityTrend: "Overall maturity across the campaign's closed waves.",
    lamaParticipationTrend: "Participation and coverage across the campaign's closed waves.",
    lamaCategoryResults: "Per-category current, previous, delta, sparkline, and target rows.",
    lamaPillarResults: "Per-pillar current, previous, delta, sparkline, and target rows.",
    lamaMaturityHierarchy: "Overall maturity in percent with pillar coverage, a pillar radar, and exact pillar scores — baseline, responses, or both.",
    lamaHierarchyResults: "Pillar → category → question results in percent, with baseline-to-responses change.",
    lamaTeamHeatmap: "Team-by-wave maturity heatmap.",
    lamaTopMovers: "Most improved and slipping assessment areas.",
    lamaPriorityRoadmap: "Lowest-scoring areas from the latest wave as a prioritized roadmap.",
};
/** JSON Schema export — AI tool-call signatures. */
const generatedWidgetJsonSchemas = Object.fromEntries(WIDGET_KINDS.map((kind) => [
    kind,
    zodToJsonSchema(WIDGET_PARAMS_SCHEMAS[kind], { $refStrategy: "none" }),
]));
const quickAccessJsonSchema = generatedWidgetJsonSchemas.quickAccess;
export const WIDGET_JSON_SCHEMAS = {
    ...generatedWidgetJsonSchemas,
    // zod-to-json-schema unwraps ZodEffects and cannot express superRefine.
    // Mirror Quick Access' two data-integrity rules for discovery/AI consumers.
    quickAccess: {
        ...quickAccessJsonSchema,
        properties: {
            ...quickAccessJsonSchema.properties,
            selectedModuleIds: {
                ...quickAccessJsonSchema.properties?.["selectedModuleIds"],
                uniqueItems: true,
            },
        },
        allOf: [
            {
                if: {
                    properties: { mode: { const: "selected" } },
                    required: ["mode"],
                },
                then: {
                    properties: { selectedModuleIds: { minItems: 1 } },
                    required: ["selectedModuleIds"],
                },
            },
        ],
    },
};
/**
 * JSON Schema of the retired TAGS v2 payload (free-text Trend). The published
 * Player AI widget-contract catalogues V3–V5 embed it, standalone and inside
 * `charter`, and are resolved by exact checksum, so it must stay byte-identical
 * to what those catalogues shipped with. It validates nothing live.
 */
export const RETIRED_TAGS_V2_JSON_SCHEMA = zodToJsonSchema(RetiredTagsParamsV2Schema, { $refStrategy: "none" });
/**
 * JSON Schemas of the three kinds whose items had NO stable id before PB-4923.
 *
 * Catalogues V3–V9 published these bodies and are resolved by exact checksum,
 * so adding `id` to the live schemas must not reach them. Generated from
 * retained Zod shapes rather than pasted as literals, for the same reason
 * `RETIRED_TAGS_V2_JSON_SCHEMA` is: a literal drifts silently, a schema is
 * type-checked. `retired-json-schemas.spec.ts` pins each one against the bytes
 * captured from the tree before the change.
 */
export const RETIRED_CAUSAL_CHAIN_V1_JSON_SCHEMA = zodToJsonSchema(RetiredCausalChainParamsV1Schema, { $refStrategy: "none" });
export const RETIRED_RUN_CHART_V1_JSON_SCHEMA = zodToJsonSchema(RetiredRunChartParamsV1Schema, { $refStrategy: "none" });
export const RETIRED_ESCALATION_MATRIX_V1_JSON_SCHEMA = zodToJsonSchema(RetiredEscalationMatrixParamsV1Schema, {
    $refStrategy: "none",
});
/**
 * JSON Schema of the KPI Tile BEFORE it became a view of a KPI Manager KPI.
 *
 * Catalogues V3–V11 published this body — standalone as the tile's params, and
 * again inside the generic `widget.read` / `canvas.inspect_widget` output
 * schemas — and are resolved by exact checksum, so the binding must not reach
 * them. Same device, same reason, as the three above.
 */
export const RETIRED_KPI_TILE_V1_JSON_SCHEMA = zodToJsonSchema(RetiredKpiTileParamsV1Schema, { $refStrategy: "none" });
/**
 * The tile's description as catalogues V3–V11 published it. A description is
 * part of the catalogue body, so re-wording the live one would move four
 * published checksums exactly as a schema change would.
 */
export const RETIRED_KPI_TILE_V1_DESCRIPTION = "Pinned single-metric tile with sparkline + tone badge against target.";
/**
 * Discovery envelope for `GET /v1/widgets/kinds`. Only an AI-discoverable
 * subset of `ENTITY_WIDGET_KINDS` is advertised (imageTile/linkTile are
 * excluded). Entity creation derives from `WIDGET_INSTANCE_STORES`, not
 * `WIDGET_STORAGE`, which describes rendering/storage mode. That
 * excludes both the Yjs-native Hoshin canvas kinds (PB-234) and the
 * dashboard-instance-only LAMA report kinds, which are `storage: "sql"` and so
 * would have leaked through the old storage-based filter.
 */
export function getWidgetKindCatalogue() {
    return AI_DISCOVERABLE_WIDGET_KINDS.map((kind) => ({
        kind,
        label: WIDGET_LABELS[kind],
        description: WIDGET_DESCRIPTIONS[kind],
        jsonSchema: WIDGET_JSON_SCHEMAS[kind],
        defaultParams: WIDGET_DEFAULT_PARAMS[kind],
    }));
}
/** Validate raw params against a kind's schema. Throws ZodError on mismatch. */
export function validateWidgetParams(kind, raw, schemaVersion) {
    return getWidgetParamsSchema(kind, schemaVersion).parse(raw);
}
//# sourceMappingURL=registry.js.map